import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import { MachineModel as OptionType } from "interfaces/machine-model";
import { Autocomplete } from "components/Autocomplete";
import { useLazyGetMachineModels } from "hooks/machine-models/useLazyGetMachineModels";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface MachineModelFilterProps {
  selectedMachineModels: OptionType[];
  onChangeSelectedMachineModels: (values: OptionType[]) => void;
  showChips?: boolean;
}

const selectAllOption: OptionType = {
  id: -1,
  name: "Select All",
  manufacturer: 0,
  rated_power_kw: 0,
  create_user: 0,
  change_user: 0,
  create_date: "",
  change_date: ""
};

export const MachineModelFilter = ({
  selectedMachineModels,
  onChangeSelectedMachineModels,
  showChips = true
}: MachineModelFilterProps): JSX.Element => {
  const {
    loading: manufacturerLoading,
    error: manufacturerError,
    fetchMachineModel: getMachineModels
  } = useLazyGetMachineModels();
  const [options, setOptions] = useState<OptionType[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    /** If it is the first focus event, get options. */
    if (options === undefined) {
      getMachineModels().then(response => {
        if (response.results) {
          setOptions([selectAllOption, ...response.results]);
        }
      });
    }
    setSelectedState(selectedMachineModels);
  }, [options, getMachineModels, selectedMachineModels]);

  return (
    <StyledAutocomplete
      label="Model"
      placeholder={showChips ? "Model" : ""}
      loading={manufacturerLoading}
      error={manufacturerError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options ?? []}
      noOptionsText="No results found"
      getOptionLabel={(option: OptionType) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedMachineModels}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;
        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");
        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: OptionType[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              option =>
                !filteredSelectedState.some(
                  selected => selected.id === option.id
                )
            );
            onChangeSelectedMachineModels(selected);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedMachineModels, ...filteredNonSelectAllOptions],
              "id"
            );

            // If not everything was selected, select all
            onChangeSelectedMachineModels(selected);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedMachineModels(values);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
